import axios from "axios";
import { API_BASE_URL } from "../config";

const api = axios.create({
    baseURL: API_BASE_URL,
});

// Request interceptor: Attach accessToken to every outgoing request
api.interceptors.request.use((config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Response interceptor: Handle errors (401, 403) and implement token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // 1. Handle 401 Unauthorized (Expired or Invalid Access Token)
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            const refreshToken = localStorage.getItem("refreshToken");

            if (refreshToken) {
                try {
                    // Attempt to get a new access token using the refresh token
                    const refreshRes = await axios.post(`${API_BASE_URL}token/refresh/`, {
                        refresh: refreshToken
                    });

                    if (refreshRes.status === 200) {
                        const newAccess = refreshRes.data.access;
                        localStorage.setItem("accessToken", newAccess);
                        
                        // Update access token cookie
                        document.cookie = `access_token=${newAccess}; path=/; max-age=86400; SameSite=Lax`;

                        // Update the authorization header and retry original request
                        api.defaults.headers.common["Authorization"] = `Bearer ${newAccess}`;
                        originalRequest.headers["Authorization"] = `Bearer ${newAccess}`;
                        return api(originalRequest);
                    }
                } catch (refreshError) {
                    console.error("Token refresh failed. Redirecting to login...", refreshError);
                    // Refresh token is also invalid/expired - full logout
                    handleLogout();
                }
            } else {
                handleLogout();
            }
        }

        // 2. Handle 403 Forbidden (Missing Permissions or Organization Suspended)
        if (error.response && error.response.status === 403) {
            const detail = error.response.data?.detail || "";
            
            // If organization is suspended, force immediate logout
            if (detail.includes("Your organization has been suspended")) {
                console.error("Organization suspended. Enforcing immediate logout.");
                handleLogout();
                return Promise.reject(error);
            }

            console.warn("Access Forbidden: Syncing RBAC and Redirecting...");
            localStorage.removeItem("navigation"); // Invalidate cache
            window.dispatchEvent(new Event("rbac_update")); // Tell AuthContext to resync
            
            const storedUser = localStorage.getItem("user");
            let isAgency = false;
            if (storedUser) {
                try {
                    const u = JSON.parse(storedUser);
                    if (u.user_type === 'agency') {
                        isAgency = true;
                    }
                } catch (e) {}
            }
            const targetPath = isAgency ? '/agency-portal' : '/dashboard';
            if (!window.location.pathname.includes(targetPath) && !window.location.pathname.startsWith("/meetings/room/")) {
                window.location.href = targetPath;
            }
        }

        return Promise.reject(error);
    }
);

/**
 * Helper to generate an authenticated media URL for direct browser links or image tags
 */
export const getSecureMediaUrl = (url) => {
    if (!url) return "";
    const token = localStorage.getItem("accessToken");
    if (!token) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}token=${token}`;
};

/**
 * Helper to clear local state and redirect to login
 */
function handleLogout() {
    // If currently in an active meeting room, do not force-redirect to login page
    if (window.location.pathname.startsWith("/meetings/room/")) {
        console.warn("API 401 intercepted in meeting room; skipping redirect to login.");
        return;
    }

    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("isAuth");
    localStorage.removeItem("navigation");
    
    // Redirect only if not already on the login page
    if (window.location.pathname !== "/" && window.location.pathname !== "/login") {
        window.location.href = "/";
    }
}

export default api;

